/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { AxiosError } from 'axios';
import { createBaseErrorHandler } from 'src/core/utils/interceptors/errorHandlers/createBaseErrorHandler';

/**
 * Represents a handler for xml error objects that are returned from
 * azure old arm APIs.
 */
export const createAzureXmlErrorHandler = () => {
    const baseActions = createBaseErrorHandler();

    return {
        ...baseActions,

        /**
         * Handles the error response returned from an old azure arm API.
         *
         * @param error The error object thrown.
         * @returns A formatted Error object.
         */
        handle: async (error: AxiosError) => {
            if (!error.response) {
                return baseActions.delegate(error);
            }
            const errorStringifiedBody = <string>error.response?.data;
            const errorObject: Error = new Error();

            if (error.response.status && error.response.statusText) {
                errorObject.name = error.response.status.toString();
                errorObject.message = error.response.statusText;

                return errorObject;
            }

            if (!errorStringifiedBody?.includes('<?xml')) {
                return baseActions.delegate(error);
            }

            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(errorStringifiedBody, 'text/xml');
            const xmlErrorObj = xmlDoc.getElementsByTagName('Error')[0];
            const errorCode = xmlErrorObj.getElementsByTagName('Code')[0].textContent;
            const errorMessage = xmlErrorObj.getElementsByTagName('Message')[0].textContent;

            errorObject.name = errorCode;
            errorObject.message = errorMessage;

            return errorObject;
        }
    };
};
